import React, { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FREE = { id: "free", name: "Free Starter", display: "$0", period: "forever", features: ["Random video chat", "Basic interest matching", "Report / block", "Community access"] };

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function ctaLabel(t, isCurrent) {
  if (isCurrent) return "Current plan";
  if (t.id === "free") return "Start free";
  return `Go ${t.name.split(" ")[0]}`;
}

export default function Pricing() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [configured, setConfigured] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then((r) => { setPlans(r.data.plans); setConfigured(r.data.configured); }).catch(() => setConfigured(false));
  }, []);

  const buy = async (plan) => {
    if (!user) { nav("/auth?tab=register"); return; }
    if (!configured) { toast.info("Payments are in setup mode — Razorpay keys not configured yet."); return; }
    setBusy(plan.id);
    try {
      const { data } = await api.post("/billing/order", { plan_id: plan.id });
      if (!(await loadRazorpay())) throw new Error("Could not load Razorpay checkout");
      const rz = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "Orbit Hello", description: `${plan.name} ${plan.period}`, prefill: data.prefill,
        theme: { color: "#22d3ee" },
        handler: async (res) => {
          try {
            const v = await api.post("/billing/verify", res);
            setUser(v.data.user);
            toast.success(`${plan.name} activated — welcome aboard!`);
            nav("/dashboard");
          } catch (e) { toast.error(apiError(e)); }
        },
        modal: { ondismiss: () => { setBusy(null); toast("Checkout closed — no charge made."); } },
      });
      rz.on("payment.failed", (r) => { setBusy(null); toast.error(r?.error?.description || "Payment failed"); });
      rz.open();
    } catch (e) {
      toast.error(apiError(e));
      setBusy(null);
    }
  };

  const tiers = [FREE, ...plans.map((p) => ({ ...p, highlight: p.id === "vip" }))];
  let currentPlan = null;
  if (user) currentPlan = user.premium ? user.plan : "free";

  return (
    <div className="min-h-screen orbit-bg-grid">
      <Nav />
      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-center mb-14">
          <span className="orbit-chip active"><Sparkles className="w-3 h-3" /> Simple pricing</span>
          <h1 className="font-display text-5xl font-extrabold mt-4">Upgrade your Orbit Hello</h1>
          <p className="text-slate-400 mt-3 max-w-lg mx-auto">Mixed matching is always free. Unlock gender filters (men only / women only), country filters, translation and priority matching. One-time purchase, no auto-renewal.</p>
        </div>

        {configured === false && (
          <div className="max-w-2xl mx-auto mb-8 orbit-glass rounded-2xl px-5 py-3 flex items-center gap-3 border border-amber-500/40 text-sm text-amber-200" data-testid="pricing-setup-banner">
            <ShieldAlert className="w-4 h-4 shrink-0" /> Payments are in setup mode. Add your Razorpay keys to enable checkout.
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {tiers.map((t) => {
            const isCurrent = currentPlan === t.id;
            return (
              <div key={t.id} className={`orbit-glass rounded-3xl p-8 relative ${t.highlight ? "border-cyan-500/50 orbit-glow-cyan" : ""}`} data-testid={`pricing-${t.id}`}>
                {t.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 orbit-chip active text-xs">MOST POPULAR</div>}
                <div className="font-display text-xl font-bold">{t.name}</div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-extrabold" data-testid={`pricing-price-${t.id}`}>{t.display}</span>
                  <span className="text-slate-500 text-sm">{t.period}</span>
                </div>
                {t.usd && <div className="text-xs font-mono text-slate-500 mt-1">≈ {t.usd} · billed in {t.currency}</div>}
                <button
                  disabled={busy === t.id || isCurrent}
                  onClick={() => (t.id === "free" ? nav(user ? "/dashboard" : "/auth") : buy(t))}
                  className={`w-full mt-6 py-3 rounded-full font-semibold transition disabled:opacity-60 flex items-center justify-center gap-2 ${t.highlight ? "orbit-btn-primary" : "bg-white/5 border border-white/10 text-slate-100 hover:bg-white/10"}`}
                  data-testid={`pricing-cta-${t.id}`}
                >
                  {busy === t.id && <Loader2 className="w-4 h-4 animate-spin" />}
                  {ctaLabel(t, isCurrent)}
                </button>
                <ul className="mt-6 space-y-2">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-cyan-300 shrink-0" /> {f}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-slate-500 mt-10 font-mono">Secure checkout by Razorpay · UPI, cards, netbanking & wallets · Plans unlock instantly for their period.</p>
      </main>
    </div>
  );
}
